import { Heart, MessageCircle, Send, Bookmark, MoreHorizontal } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { PostPreview } from '../../post-preview';
import { forwardRef, useState } from 'react';
import type { UserTag } from '../../../shared/lib/instagramTags';

interface MediaItem {
  url: string;
  type: 'image' | 'video';
}

interface TemplateData {
  backgroundColor: string;
  textColor: string;
  useTwoColors?: boolean;
  textColorSecondary?: string;
  fontFamily: string;
  fontUrl?: string;
  fontName?: string; // ← Добавляем fontName
  fontSize?: number; // ← Добавляем fontSize
  logoUrl?: string;
  logoSize?: number;
  text?: string;
  useGradient?: boolean;
  backgroundColorEnd?: string;
  backgroundAlpha?: number;
  backgroundAlphaEnd?: number;
  logoPosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center';
  textUppercase?: boolean;
  textLineSpacing?: number;
  fontBold?: boolean;
  showLine?: boolean;
  lineColor?: string;
  lineHeight?: number;
  linePosition?: 'top' | 'bottom';
}

interface InstagramPostPreviewProps {
  // Instagram специфичные пропсы
  username: string;
  avatarUrl?: string;
  profilePicture?: string;
  caption?: string;
  showActions?: boolean;
  
  // Пропсы от PostPreview
  mode?: 'simple' | 'template';
  imageUrl?: string;
  mediaType?: 'image' | 'video';
  mediaItems?: MediaItem[];
  onImageUpload?: (files: File[]) => void; // Изменили на массив File[]
  templateData?: TemplateData;
  aspectRatio?: string;
  showUploadButton?: boolean;
  applyTemplateToFirst?: boolean; // Применить шаблон только к первому элементу карусели
  fitMode?: 'fill' | 'fit'; // Режим отображения медиа
  userTags?: UserTag[];
  onUserTagsChange?: (next: UserTag[]) => void;
  /** Рендер сообщает подобранный кегль и потолок плашки, preview-space px. */
  onResolvedFontSize?: (info: { fontSize: number; max: number }) => void;
}

export const InstagramPostPreview = forwardRef<HTMLDivElement, InstagramPostPreviewProps>(({
  // Instagram пропсы
  username,
  avatarUrl,
  profilePicture,
  caption,
  showActions = true,
  
  // PostPreview пропсы
  mode = 'simple',
  imageUrl,
  mediaType,
  mediaItems,
  onImageUpload,
  templateData,
  onResolvedFontSize,
  aspectRatio = 'square',
  showUploadButton = true,
  applyTemplateToFirst = false,
  fitMode = 'fill',
  userTags,
  onUserTagsChange,
}, ref) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const isCarousel = mediaItems && mediaItems.length > 1;

  const handleSlideChange = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <div className="bg-white rounded-lg border max-w-md mx-auto overflow-hidden">
      {/* Шапка поста */}
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-3">
          <Avatar className="h-8 w-8">
            {/* key по src — свежий <img> при смене аккаунта, без "прилипшего" кадра */}
            <AvatarImage key={avatarUrl || profilePicture || username} src={avatarUrl || profilePicture} alt={username} />
            <AvatarFallback>
              {username.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span className="font-semibold text-sm">{username}</span>
        </div>
        <button className="text-gray-900 hover:text-gray-600 transition-colors">
          <MoreHorizontal className="h-5 w-5" />
        </button>
      </div>

      {/* Медиа контент - используем PostPreview с передачей ref */}
      <div className="[&>div]:space-y-0 [&>div>div]:rounded-none [&>div>div]:border-0 [&>div>div]:max-w-none">
        <PostPreview
          ref={ref}
          mode={mode}
          imageUrl={imageUrl}
          mediaType={mediaType}
          mediaItems={mediaItems}
          onImageUpload={onImageUpload}
          templateData={templateData}
          onResolvedFontSize={onResolvedFontSize}
          aspectRatio={aspectRatio}
          showUploadButton={showUploadButton}
          applyTemplateToFirst={applyTemplateToFirst}
          currentSlide={currentSlide}
          onSlideChange={handleSlideChange}
          fitMode={fitMode}
          userTags={userTags}
          onUserTagsChange={onUserTagsChange}
        />
      </div>

      {/* Индикаторы карусели */}
      {isCarousel && (
        <div className="py-3 flex justify-center gap-1.5">
          {mediaItems.map((_, index) => (
            <button
              key={index}
              onClick={() => handleSlideChange(index)}
              className={`h-2 w-2 rounded-full transition-all ${
                index === currentSlide 
                  ? 'bg-blue-500' 
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Перейти к слайду ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Действия */}
      {showActions && (
        <div className="p-3 space-y-2">
          {/* Иконки действий */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button className="text-gray-900 hover:text-gray-600 transition-colors">
                <Heart className="h-6 w-6" />
              </button>
              <button className="text-gray-900 hover:text-gray-600 transition-colors">
                <MessageCircle className="h-6 w-6" />
              </button>
              <button className="text-gray-900 hover:text-gray-600 transition-colors">
                <Send className="h-6 w-6" />
              </button>
            </div>
            <button className="text-gray-900 hover:text-gray-600 transition-colors">
              <Bookmark className="h-6 w-6" />
            </button>
          </div>

          {/* Описание */}
          <div className="text-sm">
            <span className="font-semibold mr-2">{username}</span>
            <span className="text-gray-900">{caption || 'Без описания'}</span>
          </div>
        </div>
      )}
    </div>
  );
});

InstagramPostPreview.displayName = 'InstagramPostPreview';